import { formatPhoneForCountry } from '@shared/lib/format/masks'
import {
  isValidCNPJ,
  isValidCPF,
  normalizePhoneForCountry,
  onlyDigits,
} from '@shared/lib/validation/documents'
import type { UserProfile } from '@shared/lib/auth/session-types'
import type { IdentityRejection, KycRequirement } from '../api/verification'
import {
  getRequirementFieldLabel,
  getRequirementSummaryLabel,
  getSafeRequirementLabel,
  isRepresentativeAddressRequirement,
} from '../lib/requirement-label'
import type { BusinessProfileFieldsValue } from './ProfileBusinessFields'
import type { RepresentativeFieldsValue } from './ProfileRepresentativeFields'

export type ProfileForm = {
  businessType: 'INDIVIDUAL' | 'COMPANY'
  firstName: string
  lastName: string
  birthDate: string
  legalName: string
  businessName: string
  phone: string
  documentNumber: string
  politicalExposure: '' | 'none' | 'existing'
  businessProfile: BusinessProfileFieldsValue
  representativeDocumentNumber: string
  representativeTitle: string
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
}

export type ProfileFieldError = Partial<
  Record<
    keyof ProfileForm | keyof BusinessProfileFieldsValue | keyof RepresentativeFieldsValue,
    string
  >
>

export type ProfileField = keyof ProfileFieldError

export const stateOptions = [
  'AC',
  'AL',
  'AP',
  'AM',
  'BA',
  'CE',
  'DF',
  'ES',
  'GO',
  'MA',
  'MT',
  'MS',
  'MG',
  'PA',
  'PB',
  'PR',
  'PE',
  'PI',
  'RJ',
  'RN',
  'RS',
  'RO',
  'RR',
  'SC',
  'SP',
  'SE',
  'TO',
].map((value) => ({ label: value, value }))

export function profileIsComplete(profile: UserProfile | null | undefined): boolean {
  if (!profile) return false

  const hasValue = (value: string | undefined): boolean => Boolean(value?.trim())
  const address = profile.address
  const commonFields =
    hasValue(profile.businessType) &&
    hasValue(profile.documentType) &&
    hasValue(profile.documentNumber) &&
    hasValue(profile.firstName) &&
    hasValue(profile.lastName) &&
    hasValue(profile.birthDate) &&
    hasValue(profile.phone) &&
    profile.politicalExposure !== undefined &&
    hasValue(address?.line1) &&
    hasValue(address?.city) &&
    hasValue(address?.state) &&
    hasValue(address?.postalCode) &&
    hasValue(profile.businessProfile?.url) &&
    hasValue(profile.businessProfile?.productDescription) &&
    hasValue(profile.businessProfile?.mcc)

  if (!commonFields) return false
  if (profile.businessType !== 'COMPANY') return true

  // The trade name (businessName) is optional: a company without one is saved with its legal name.
  return (
    hasValue(profile.legalName) &&
    hasValue(profile.representative?.documentNumber) &&
    hasValue(profile.representative?.title)
  )
}

/** One-line summary of a saved profile for the collapsed step: name, masked document, city/UF. */
export function profileSummary(profile: UserProfile | null | undefined): string {
  const name =
    profile?.businessType === 'COMPANY'
      ? profile.legalName || `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim()
      : `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`.trim()
  const document = maskDocument(profile?.documentNumber)
  const city = profile?.address?.city ?? ''
  const state = profile?.address?.state ?? ''
  return [name || 'Dados cadastrais', document, city && state ? `${city}/${state}` : city || state]
    .filter(Boolean)
    .join(' · ')
}

function maskDocument(value: string | undefined): string {
  const digits = onlyDigits(value ?? '')
  const lastDigits = digits.slice(-2)
  if (digits.length === 14) return `**.***.***/****-${lastDigits}`
  return `***.***.***-${lastDigits}`
}

/** Form state from the saved profile; the signup name and phone fill an empty profile. */
export function profileFromUser(
  profile: UserProfile | null | undefined,
  fallbackName?: string,
  fallbackPhone?: string,
): ProfileForm {
  const rawFirst = profile?.firstName ?? ''
  const rawLast = profile?.lastName ?? ''
  let firstName = rawFirst
  let lastName = rawLast
  if (!rawFirst && !rawLast && fallbackName?.trim()) {
    const parts = fallbackName.trim().split(/\s+/)
    firstName = parts[0] ?? ''
    lastName = parts.slice(1).join(' ')
  }
  return {
    businessType:
      profile?.businessType === 'COMPANY' || profile?.documentType === 'CNPJ'
        ? 'COMPANY'
        : 'INDIVIDUAL',
    firstName,
    lastName,
    birthDate: profile?.birthDate ?? '',
    legalName: profile?.legalName ?? '',
    businessName: profile?.businessName ?? '',
    phone: profile?.phone
      ? formatPhoneForCountry(profile.phone, 'BR')
      : fallbackPhone
        ? formatPhoneForCountry(fallbackPhone, 'BR')
        : '',
    documentNumber: profile?.documentNumber ?? '',
    politicalExposure: profile?.politicalExposure ?? '',
    businessProfile: {
      url: profile?.businessProfile?.url ?? '',
      productDescription: profile?.businessProfile?.productDescription ?? '',
      mcc: profile?.businessProfile?.mcc ?? '',
    },
    representativeDocumentNumber: profile?.representative?.documentNumber ?? '',
    representativeTitle: profile?.representative?.title ?? '',
    line1: profile?.address?.line1 ?? '',
    line2: profile?.address?.line2 ?? '',
    city: profile?.address?.city ?? '',
    state: profile?.address?.state ?? '',
    postalCode: profile?.address?.postalCode ?? '',
  }
}

export function sameProfileForm(a: ProfileForm, b: ProfileForm): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function profileFieldError(
  field: ProfileField,
  form: ProfileForm,
  businessType: ProfileForm['businessType'],
): string | undefined {
  switch (field) {
    case 'firstName':
      return form.firstName.trim().length < 2 ? 'Informe um nome válido.' : undefined
    case 'lastName':
      return form.lastName.trim().length < 2 ? 'Informe um sobrenome válido.' : undefined
    case 'birthDate':
      return isValidIsoDate(form.birthDate) ? undefined : 'Informe uma data válida.'
    case 'legalName':
      return businessType === 'COMPANY' && form.legalName.trim().length < 2
        ? 'Informe a razão social.'
        : undefined
    case 'representativeDocumentNumber':
      return businessType === 'COMPANY' && !isValidCPF(form.representativeDocumentNumber)
        ? 'Informe um CPF válido.'
        : undefined
    case 'representativeTitle':
      return businessType === 'COMPANY' &&
        (!form.representativeTitle.trim() || form.representativeTitle.trim().length > 100)
        ? 'Informe um cargo com até 100 caracteres.'
        : undefined
    case 'url': {
      const url = form.businessProfile.url.trim()
      if (!url) return 'Informe o site do negócio.'
      return isAbsoluteHttpUrl(url)
        ? undefined
        : 'Informe um site com endereço completo, iniciado por http:// ou https://.'
    }
    case 'productDescription': {
      const description = form.businessProfile.productDescription.trim()
      if (!description) return 'Descreva o negócio.'
      return description.length < 10 || description.length > 400
        ? 'Use uma descrição entre 10 e 400 caracteres.'
        : undefined
    }
    case 'mcc': {
      const mcc = onlyDigits(form.businessProfile.mcc)
      if (!mcc) return 'Selecione a categoria do negócio.'
      return mcc.length !== 4 ? 'Selecione uma categoria válida.' : undefined
    }
    case 'documentNumber': {
      const documentDigits = onlyDigits(form.documentNumber)
      const identity = documentIdentity(documentDigits)
      if (documentDigits.length !== 11 && documentDigits.length !== 14) {
        return 'Informe um CPF com 11 ou um CNPJ com 14 dígitos.'
      }
      const isValid =
        identity !== null &&
        (identity.documentType === 'CNPJ'
          ? isValidCNPJ(documentDigits)
          : isValidCPF(documentDigits))
      return isValid ? undefined : `Informe um ${identity?.documentType ?? 'documento'} válido.`
    }
    case 'phone':
      return normalizePhoneForCountry(form.phone, 'BR') === null
        ? 'Confira o telefone informado.'
        : undefined
    case 'politicalExposure':
      return form.politicalExposure === '' ? 'Selecione uma opção.' : undefined
    case 'line1':
      return form.line1.trim().length < 3 ? 'Informe o endereço.' : undefined
    case 'city':
      return form.city.trim().length < 2 ? 'Informe a cidade.' : undefined
    case 'state':
      return form.state.length !== 2 ? 'Selecione o estado.' : undefined
    case 'postalCode':
      return onlyDigits(form.postalCode).length !== 8 ? 'Informe o CEP completo.' : undefined
    default:
      return undefined
  }
}

/**
 * Fields validated on save, in the order errors are reported. Every field is required except the
 * address complement (line2) and the company's trade name (businessName), which falls back to the
 * legal name on save.
 */
const validatedFields: readonly ProfileField[] = [
  'firstName',
  'lastName',
  'birthDate',
  'legalName',
  'representativeDocumentNumber',
  'representativeTitle',
  'url',
  'productDescription',
  'mcc',
  'documentNumber',
  'phone',
  'politicalExposure',
  'line1',
  'city',
  'state',
  'postalCode',
]

export function profileErrors(
  form: ProfileForm,
  businessType: ProfileForm['businessType'],
): ProfileFieldError {
  const errors: ProfileFieldError = {}
  for (const field of validatedFields) {
    const error = profileFieldError(field, form, businessType)
    if (error) errors[field] = error
  }
  return errors
}

/** Hint on a field to check against the others; the page also uses it to find the first flagged field. */
export const CHECK_FIELD_HINT = 'Confira este campo.'

export const COMPANY_ADDRESS_CHECK_HINT = 'Verifique se é o endereço registrado no CNPJ.'

export function verificationFailureMessage(requirement: KycRequirement): string | null {
  if (requirement.errorCode !== 'verification_failed_other' || requirement.resolution !== 'fix')
    return null

  if (isRepresentativeAddressRequirement(requirement.id)) {
    return 'Não foi possível confirmar o endereço residencial do representante legal. Fale com o suporte da VendePay para orientação.'
  }
  const label =
    getRequirementSummaryLabel(requirement.id, 'form') ??
    getSafeRequirementLabel(requirement.id, 'form')
  return `Não foi possível confirmar: ${label}. Confira os dados informados. Se precisar de orientação, fale com o suporte da VendePay.`
}

/**
 * The form's open requirements as field errors and hints, following each requirement's
 * resolution: a refused value is an error, values that did not match each other are a soft hint,
 * and a field requested again is confirmed (only a hint when another field failed, since it is
 * part of the same check). Requirements under review, waiting for the company's people, or with a refused document show nothing here, and
 * those without an editable field are named in a list.
 */
export function correctionFieldErrors(
  requirements: readonly KycRequirement[],
  lockedFields: ReadonlySet<ProfileField> = new Set(),
  registrationFeedback = false,
): {
  fieldErrors: ProfileFieldError
  fieldHints: ProfileFieldError
  combinationLabels: string[]
  unmatchedLabels: string[]
  companyAddressCheck: boolean
  verificationMessages: string[]
} {
  const fieldErrors: ProfileFieldError = {}
  const fieldHints: ProfileFieldError = {}
  const confirmationFields = new Set<ProfileField>()
  const combinationLabels: string[] = []
  const unmatchedLabels: string[] = []
  const verificationMessages: string[] = []
  let companyAddressCheck = false
  for (const requirement of requirements) {
    if (
      requirement.resolution === 'review' ||
      requirement.resolution === 'awaiting_people' ||
      requirement.resolution === 'awaiting_document' ||
      requirement.resolution === 'document_refused'
    )
      continue
    const field =
      requirement.field !== null &&
      isProfileField(requirement.field) &&
      !lockedFields.has(requirement.field)
        ? requirement.field
        : null
    const label =
      getRequirementSummaryLabel(requirement.id, 'form') ??
      getSafeRequirementLabel(requirement.id, 'form')
    const verificationMessage = registrationFeedback
      ? verificationFailureMessage(requirement)
      : null
    if (verificationMessage) {
      if (!verificationMessages.includes(verificationMessage))
        verificationMessages.push(verificationMessage)
      if (
        field &&
        !isRepresentativeAddressRequirement(requirement.id) &&
        fieldHints[field] === undefined
      )
        fieldHints[field] = 'Não foi possível confirmar este dado.'
      continue
    }
    if (requirement.resolution === 'check') {
      if (!combinationLabels.includes(label)) combinationLabels.push(label)
      const companyAddress = requirement.id.startsWith('company.address.')
      if (companyAddress) companyAddressCheck = true
      if (field && fieldHints[field] !== COMPANY_ADDRESS_CHECK_HINT) {
        fieldHints[field] = companyAddress ? COMPANY_ADDRESS_CHECK_HINT : CHECK_FIELD_HINT
      }
      continue
    }
    if (!field) {
      if (!unmatchedLabels.includes(label)) unmatchedLabels.push(label)
      continue
    }
    if (requirement.resolution === 'fix') {
      const fieldLabel = getRequirementFieldLabel(requirement.id, 'form') ?? label
      fieldErrors[field] = `${fieldLabel} precisa ser corrigido.`
    } else {
      confirmationFields.add(field)
    }
  }
  const anotherFieldFailed =
    Object.keys(fieldErrors).length > 0 ||
    combinationLabels.length > 0 ||
    verificationMessages.length > 0
  for (const field of confirmationFields) {
    if (fieldErrors[field] !== undefined || fieldHints[field] !== undefined) continue
    if (anotherFieldFailed) fieldHints[field] = CHECK_FIELD_HINT
    else fieldErrors[field] = 'Confirme este campo.'
  }
  return {
    fieldErrors,
    fieldHints,
    combinationLabels,
    unmatchedLabels,
    companyAddressCheck,
    verificationMessages,
  }
}

export function documentIdentity(
  value: string,
):
  | { readonly businessType: 'INDIVIDUAL'; readonly documentType: 'CPF' }
  | { readonly businessType: 'COMPANY'; readonly documentType: 'CNPJ' }
  | null {
  const digits = onlyDigits(value)
  if (digits.length === 11) return { businessType: 'INDIVIDUAL', documentType: 'CPF' }
  if (digits.length === 14) return { businessType: 'COMPANY', documentType: 'CNPJ' }
  return null
}

function isAbsoluteHttpUrl(value: string): boolean {
  if (!/^https?:\/\/[^/\\]/i.test(value) || /[\s\\]/.test(value)) return false
  if (/^https?:\/\/.*https?:\/\//i.test(value.split(/[?#]/, 1)[0] ?? '')) return false
  try {
    const url = new URL(value)
    if (url.username || url.password) return false
    const validHost =
      url.hostname.startsWith('[') ||
      url.hostname
        .replace(/\.$/, '')
        .split('.')
        .every((label) => /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label))
    return (url.protocol === 'http:' || url.protocol === 'https:') && validHost
  } catch {
    return false
  }
}

function isValidIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
  const parsed = new Date(`${value}T00:00:00.000Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

const FIELD_NAMES: Record<ProfileField, string> = {
  businessType: 'o tipo de cadastro',
  firstName: 'o nome',
  lastName: 'o sobrenome',
  birthDate: 'a data de nascimento',
  legalName: 'a razão social',
  businessName: 'o nome fantasia',
  phone: 'o telefone',
  documentNumber: 'o CPF ou CNPJ',
  politicalExposure: 'a opção de pessoa politicamente exposta',
  businessProfile: 'os dados do negócio',
  representativeDocumentNumber: 'o CPF do representante',
  representativeTitle: 'o cargo',
  line1: 'o endereço',
  line2: 'o complemento',
  city: 'a cidade',
  state: 'o estado',
  postalCode: 'o CEP',
  url: 'o site do negócio',
  productDescription: 'a descrição do negócio',
  mcc: 'a categoria do negócio',
  title: 'o cargo',
}

function isProfileField(field: string): field is ProfileField {
  return field in FIELD_NAMES
}

/**
 * A value the identity verification refused, as a field error. Returns `field: null` when the
 * refusal points at no form field, so the caller shows it as a general message.
 */
export function identityRejectionError(rejection: IdentityRejection): {
  field: ProfileField | null
  message: string
} {
  const field = rejection.field !== null && isProfileField(rejection.field) ? rejection.field : null
  const name = field === null ? 'os dados informados' : FIELD_NAMES[field]
  switch (rejection.reason) {
    case 'missing':
      return { field, message: `Informe ${name}.` }
    case 'invalid':
      return { field, message: `Não foi possível confirmar ${name}. Confira o valor informado.` }
    case 'invalid_document':
      return { field, message: 'Confira o número do documento.' }
    default:
      return { field, message: `Não foi possível confirmar ${name}.` }
  }
}

/** Verified identity fields the form would change, compared with the saved profile. */
export function changedVerifiedFields(
  form: ProfileForm,
  saved: UserProfile | null | undefined,
  verifiedFields: readonly string[],
): ProfileField[] {
  const savedValues: Partial<Record<ProfileField, string>> = {
    firstName: saved?.firstName ?? '',
    lastName: saved?.lastName ?? '',
    birthDate: saved?.birthDate ?? '',
  }
  const formValues: Partial<Record<ProfileField, string>> = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    birthDate: form.birthDate,
  }
  return verifiedFields.filter(
    (field): field is ProfileField =>
      isProfileField(field) && field in savedValues && savedValues[field] !== formValues[field],
  )
}

/** "o nome e a data de nascimento" */
export function describeProfileFields(fields: readonly ProfileField[]): string {
  const names = fields.map((field) => FIELD_NAMES[field])
  return names.length <= 1
    ? (names[0] ?? '')
    : `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`
}
